import React, { useState, useEffect, useMemo, useRef } from "react";
import QRCode from "qrcode";
import ContactVendorleadsModal from "../../components/ContactVendorleadsModal";
import SignInModal from "../../components/SignInModal";
import Navbar from "../../components/Navbar";

const BRAND_COLOR = "#1a3f6b";
const BRAND_ACCENT_COLOR = "#10b981";
const BRAND_BLUE = "#3b82f6";
const GRAY_DARK_COLOR = "#374151";

/* ─────────── Live demo data (real DB rows) ─────────── */
const DEMO_AGENCY = {
  agency_name: "Demo Agency",
  agent_name: "John Doe",
  agent_first_name: "John",
  agent_title: "Licensed Agent",
  agent_email: "bayan+demo@vendorleads.com.au",
  agent_phone: "0400 000 000",
  agent_colour: "#000000",
  landing_page_slug: "demo-agency",
  logo_url:
    "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/agencies/demo-agency/logo_url_1774836185_14b54735.png",
  profile_image_url:
    "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/agencies/demo-agency/profile-image.jpg",
};
const AGENT_COLOUR = DEMO_AGENCY.agent_colour;

const SEVIRA = {
  obfuscated_id: "vl3272ep",
  street_number: "22",
  street_name: "Sevira",
  street_type_long: "Avenue",
  suburb: "Gledswood Hills",
  state: "NSW",
  postcode: "2557",
  bedrooms: 7,
  bathrooms: 5,
  carspaces: 2,
  listing_property_type: "House",
  land_area: 496,
  profile_slug: "22-sevira-avenue-gledswood-hills-nsw-2557",
  photo_url_full:
    "https://b.domainstatic.com.au/2020434661_1_1_251119_021840-w1600-h1200",
};
const SEVIRA_QR_URL = `https://www.homeprice.site/p/${SEVIRA.profile_slug}-${SEVIRA.obfuscated_id}?agent=${DEMO_AGENCY.landing_page_slug}&campaign=qr_code_property`;

/* Sample scan stream feed data */
const SCAN_FEED_TEMPLATES = [
  { addr: "22 Sevira Avenue", suburb: "Gledswood Hills NSW", id: "vl3272ep", x: 62, y: 58 },
  { addr: "47 Banks Avenue", suburb: "Gledswood Hills NSW", id: "vl91ka2m", x: 58, y: 52 },
  { addr: "8 Camden Valley Way", suburb: "Gledswood Hills NSW", id: "vlk2ms9q", x: 54, y: 64 },
  { addr: "12 Banksia Street", suburb: "Eastwood NSW", id: "vl7p2bcd", x: 30, y: 28 },
  { addr: "18 Garrick Road", suburb: "Liverpool NSW", id: "vl5xn8h2", x: 22, y: 70 },
  { addr: "5 Ridgeline Drive", suburb: "Gledswood Hills NSW", id: "vlqq31fr", x: 66, y: 48 },
  { addr: "31 Heritage Way", suburb: "Gledswood Hills NSW", id: "vlh8r3xc", x: 49, y: 60 },
  { addr: "104 Northbourne Avenue", suburb: "Campbelltown NSW", id: "vl21ndu7", x: 38, y: 84 },
  { addr: "7 Lakeside Crescent", suburb: "Harrington Park NSW", id: "vl4ggbb2", x: 73, y: 36 },
  { addr: "16 Macquarie Street", suburb: "Liverpool NSW", id: "vlx7vqp1", x: 18, y: 60 },
  { addr: "3 Currawong Place", suburb: "Prestons NSW", id: "vl9d1ze4", x: 26, y: 42 },
  { addr: "29 Sevira Avenue", suburb: "Gledswood Hills NSW", id: "vl3a2k0n", x: 64, y: 54 },
];

/* ─────────── Keyframes ─────────── */
function GlobalAnimations() {
  return (
    <style>{`
      @keyframes qrPulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.35); }
        50%      { box-shadow: 0 0 0 12px rgba(16,185,129,0); }
      }
      @keyframes scanLine {
        0%   { transform: translateY(0%); opacity: 0.9; }
        100% { transform: translateY(100%); opacity: 0; }
      }
      @keyframes fadeUp {
        0%   { opacity: 0; transform: translateY(8px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes slideInRight {
        0%   { opacity: 0; transform: translateX(20px); }
        100% { opacity: 1; transform: translateX(0); }
      }
      @keyframes slideInDown {
        0%   { opacity: 0; transform: translateY(-12px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes walkerBob {
        0%, 100% { transform: translate(-50%, -50%) translateY(0); }
        50%      { transform: translate(-50%, -50%) translateY(-2px); }
      }
      @keyframes barGrow {
        0%   { transform: scaleY(0.05); }
        100% { transform: scaleY(1); }
      }
      @keyframes leadPing {
        0%   { transform: scale(0.6); opacity: 0; }
        60%  { opacity: 1; }
        100% { transform: scale(1.6); opacity: 0; }
      }
      @keyframes notifShake {
        0%, 100% { transform: rotate(0deg); }
        20%      { transform: rotate(-8deg); }
        40%      { transform: rotate(8deg); }
        60%      { transform: rotate(-4deg); }
        80%      { transform: rotate(4deg); }
      }
      @keyframes pinPulse {
        0%   { transform: translate(-50%, -50%) scale(1); opacity: 0.7; }
        100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; }
      }
      @keyframes confettiFall {
        0%   { transform: translate(0, 0) rotate(0deg); opacity: 1; }
        100% { transform: translate(var(--cx, 0), var(--cy, 120px)) rotate(var(--cr, 360deg)); opacity: 0; }
      }
      @keyframes tickPop {
        0%   { transform: scale(0); opacity: 0; }
        60%  { transform: scale(1.2); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes toastSlideUp {
        0%   { transform: translateY(140%); opacity: 0; }
        100% { transform: translateY(0); opacity: 1; }
      }
      @keyframes pinDrop {
        0%   { transform: translate(-50%, -130%) scale(0.6); opacity: 0; }
        60%  { transform: translate(-50%, -95%) scale(1.05); opacity: 1; }
        100% { transform: translate(-50%, -100%) scale(1); opacity: 1; }
      }
    `}</style>
  );
}

/* ─────────── Deterministic QR grid ─────────── */
function MiniQr({ seed = "vendorleads", size = 88, fg = "#0f172a" }) {
  const cells = 17;
  const grid = useMemo(() => {
    let s = 2166136261;
    for (let i = 0; i < seed.length; i++) {
      s ^= seed.charCodeAt(i);
      s = (s * 16777619) >>> 0;
    }
    const out = [];
    for (let y = 0; y < cells; y++) {
      const row = [];
      for (let x = 0; x < cells; x++) {
        s = (s * 1664525 + 1013904223) >>> 0;
        row.push((s & 7) > 3);
      }
      out.push(row);
    }
    const stamp = (cx, cy) => {
      for (let y = 0; y < 7; y++) {
        for (let x = 0; x < 7; x++) {
          const onEdge = x === 0 || y === 0 || x === 6 || y === 6;
          const inner = x >= 2 && x <= 4 && y >= 2 && y <= 4;
          out[cy + y][cx + x] = onEdge || inner;
        }
      }
      for (let y = -1; y <= 7; y++) {
        for (let x = -1; x <= 7; x++) {
          const yy = cy + y;
          const xx = cx + x;
          if (yy < 0 || xx < 0 || yy >= cells || xx >= cells) continue;
          if (x === -1 || y === -1 || x === 7 || y === 7) out[yy][xx] = false;
        }
      }
    };
    stamp(0, 0);
    stamp(cells - 7, 0);
    stamp(0, cells - 7);
    return out;
  }, [seed]);

  const cell = size / cells;
  return (
    <div
      style={{
        width: size,
        height: size,
        background: "#fff",
        borderRadius: 4,
        padding: 3,
        boxSizing: "content-box",
        border: "1px solid #e5e7eb",
        display: "grid",
        gridTemplateColumns: `repeat(${cells}, ${cell}px)`,
        gridTemplateRows: `repeat(${cells}, ${cell}px)`,
        lineHeight: 0,
      }}
    >
      {grid.flatMap((row, y) =>
        row.map((on, x) => (
          <div
            key={`${x}-${y}`}
            style={{
              width: cell,
              height: cell,
              background: on ? fg : "transparent",
            }}
          />
        ))
      )}
    </div>
  );
}

/* ─────────── Real scannable QR (from qrcode lib) ─────────── */
function RealQr({ url, size = 104, fg = "#0f172a" }) {
  const [dataUrl, setDataUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, {
      errorCorrectionLevel: "M",
      margin: 1,
      scale: 8,
      color: { dark: fg, light: "#ffffff" },
    })
      .then((d) => {
        if (!cancelled) setDataUrl(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [url, fg]);

  return (
    <div
      style={{
        width: size,
        height: size,
        background: "#fff",
        borderRadius: 4,
        padding: 3,
        boxSizing: "content-box",
        border: "1px solid #e5e7eb",
      }}
    >
      {dataUrl && (
        <img
          src={dataUrl}
          alt="Scan QR"
          style={{ width: "100%", height: "100%", display: "block" }}
        />
      )}
    </div>
  );
}

/* ─────────── Trust chip ─────────── */
function Chip({ icon, label, colour }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 8px",
        borderRadius: 999,
        background: `${colour}1f`,
        color: colour,
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      <span style={{ fontSize: 10 }}>{icon}</span>
      {label}
    </div>
  );
}

/* ─────────── Demo Agency property flyer (mirrors PropertyQrDocument) ───────── */
function DemoPropertyFlyer({ width = 280 }) {
  const street = `${SEVIRA.street_number} ${SEVIRA.street_name} ${SEVIRA.street_type_long}`;
  const sub = `${SEVIRA.suburb}, ${SEVIRA.state} ${SEVIRA.postcode}`;

  return (
    <div
      style={{
        width,
        background: "#fff",
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid #e5e7eb",
        boxShadow: "0 20px 50px rgba(15,23,42,0.35)",
        animation: "fadeUp 0.6s ease both",
        color: "#0f172a",
      }}
    >
      {/* Agency bar: logo + agency name */}
      <div
        style={{
          background: AGENT_COLOUR,
          padding: "10px 12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <img
          src={DEMO_AGENCY.logo_url}
          alt="Demo Agency"
          style={{
            maxHeight: 22,
            maxWidth: 90,
            objectFit: "contain",
            filter: "brightness(0) invert(1)",
          }}
        />
        <div
          style={{
            color: "#fff",
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 1,
          }}
        >
          {DEMO_AGENCY.agency_name.toUpperCase()}
        </div>
      </div>

      {/* Hero photo with bed/bath/car overlay */}
      <div style={{ position: "relative", width: "100%", height: 120, background: "#0f172a" }}>
        <img
          src={SEVIRA.photo_url_full}
          alt={street}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
        <div
          style={{
            position: "absolute",
            left: 8,
            bottom: 8,
            background: "rgba(0,0,0,0.6)",
            color: "#fff",
            padding: "3px 9px",
            borderRadius: 12,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: 0.3,
          }}
        >
          {SEVIRA.bedrooms} Bed · {SEVIRA.bathrooms} Bath · {SEVIRA.carspaces} Car
        </div>
      </div>

      {/* Address */}
      <div style={{ padding: "10px 12px 4px 12px" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>{street}</div>
        <div
          style={{
            fontSize: 9,
            color: "#64748b",
            letterSpacing: 0.5,
            marginTop: 1,
            textTransform: "uppercase",
          }}
        >
          {sub}
        </div>
      </div>

      {/* CTA */}
      <div style={{ padding: "10px 10px 12px 10px", textAlign: "center" }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#111827",
            marginBottom: 9,
            lineHeight: 1.3,
          }}
        >
          See what <span style={{ color: AGENT_COLOUR, fontWeight: 800 }}>this home</span>{" "}
          is really worth,{" "}
          <span style={{ color: AGENT_COLOUR, fontWeight: 800 }}>instantly</span>
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
            justifyContent: "center",
            marginBottom: 10,
          }}
        >
          <Chip icon="⚡" label="INSTANT" colour={AGENT_COLOUR} />
          <Chip icon="✓" label="FREE" colour={AGENT_COLOUR} />
          <Chip icon="🔒" label="NO SIGN-UP" colour={AGENT_COLOUR} />
          <Chip icon="📍" label="LOCAL INSIGHTS" colour={AGENT_COLOUR} />
        </div>
        <div
          style={{
            display: "inline-block",
            position: "relative",
            animation: "qrPulse 2.2s ease-out infinite",
            borderRadius: 6,
          }}
        >
          <RealQr url={SEVIRA_QR_URL} size={104} fg="#0f172a" />
          <div
            style={{
              position: "absolute",
              inset: 4,
              overflow: "hidden",
              borderRadius: 4,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 6,
                background:
                  "linear-gradient(180deg, transparent, rgba(16,185,129,0.55))",
                animation: "scanLine 1.8s ease-in-out infinite",
              }}
            />
          </div>
        </div>
        <div
          style={{
            marginTop: 8,
            fontSize: 8,
            color: AGENT_COLOUR,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
          }}
        >
          Scan with your phone camera
        </div>
        <div
          style={{
            marginTop: 8,
            fontSize: 9,
            color: "#4b5563",
            lineHeight: 1.45,
          }}
        >
          Prefer an in-person price update?
          <br />
          Call {DEMO_AGENCY.agent_first_name} on {DEMO_AGENCY.agent_phone}
        </div>
      </div>

      {/* Agent footer: profile pic + name + title + contact */}
      <div
        style={{
          background: "#fafafa",
          borderTop: "1px solid #eef2f7",
          padding: "9px 11px",
          display: "flex",
          alignItems: "center",
          gap: 9,
        }}
      >
        <img
          src={DEMO_AGENCY.profile_image_url}
          alt={DEMO_AGENCY.agent_name}
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            objectFit: "cover",
            background: "#e5e7eb",
            flexShrink: 0,
          }}
        />
        <div style={{ lineHeight: 1.25, minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#111827" }}>
            {DEMO_AGENCY.agent_name}
          </div>
          <div style={{ fontSize: 9, color: "#6b7280" }}>
            {DEMO_AGENCY.agent_title}
          </div>
          <div style={{ fontSize: 8.5, color: "#374151", marginTop: 2, display: "flex", gap: 6 }}>
            <span style={{ color: AGENT_COLOUR, fontWeight: 800, width: 28 }}>CALL</span>
            <span>{DEMO_AGENCY.agent_phone}</span>
          </div>
          <div style={{ fontSize: 8.5, color: "#374151", marginTop: 1, display: "flex", gap: 6 }}>
            <span style={{ color: AGENT_COLOUR, fontWeight: 800, width: 28 }}>EMAIL</span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {DEMO_AGENCY.agent_email}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Live scan map (top of page) ─────────── */
function LiveScanMap({ isMobile }) {
  const [pins, setPins] = useState(() => SCAN_FEED_TEMPLATES.slice(0, 4).map((t, i) => ({ ...t, key: `seed-${i}` })));
  const counter = useRef(0);

  useEffect(() => {
    const interval = setInterval(() => {
      counter.current += 1;
      setPins((prev) => {
        const idx = counter.current % SCAN_FEED_TEMPLATES.length;
        const next = { ...SCAN_FEED_TEMPLATES[idx], key: `pin-${counter.current}` };
        return [next, ...prev].slice(0, 6);
      });
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  const newest = pins[0];

  return (
    <div
      style={{
        position: "relative",
        height: isMobile ? 320 : 420,
        borderRadius: 16,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        background: "#e8eef3",
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      <svg
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 600 420"
        style={{ position: "absolute", inset: 0, display: "block" }}
      >
        <rect width="600" height="420" fill="#e9eef3" />
        <path d="M0 340 Q 120 310 240 340 T 600 330 L600 420 L0 420 Z" fill="#cfe3ef" />
        <rect x="40" y="40" width="120" height="80" rx="10" fill="#d6ead7" />
        <rect x="420" y="60" width="130" height="90" rx="10" fill="#d6ead7" />
        <rect x="250" y="180" width="90" height="70" rx="10" fill="#d6ead7" />
        <g stroke="#ffffff" strokeWidth="9" strokeLinecap="round">
          <line x1="0" y1="160" x2="600" y2="140" />
          <line x1="0" y1="270" x2="600" y2="260" />
          <line x1="180" y1="0" x2="200" y2="420" />
          <line x1="400" y1="0" x2="420" y2="420" />
        </g>
        <g stroke="#ffffff" strokeWidth="4" strokeLinecap="round" opacity="0.9">
          <line x1="0" y1="75" x2="600" y2="65" />
          <line x1="0" y1="220" x2="600" y2="210" />
          <line x1="0" y1="350" x2="600" y2="340" />
          <line x1="90" y1="0" x2="100" y2="420" />
          <line x1="290" y1="0" x2="305" y2="420" />
          <line x1="510" y1="0" x2="520" y2="420" />
        </g>
      </svg>

      {pins.map((ev, i) => (
        <div
          key={ev.key}
          style={{
            position: "absolute",
            left: `${ev.x}%`,
            top: `${ev.y}%`,
            transform: "translate(-50%, -100%)",
            zIndex: pins.length - i,
            opacity: i === 0 ? 1 : Math.max(0.4, 1 - i * 0.13),
            animation: i === 0 ? "pinDrop 0.55s ease both" : "none",
          }}
        >
          {i === 0 && (
            <span
              style={{
                position: "absolute",
                left: "50%",
                top: "100%",
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: BRAND_BLUE,
                animation: "pinPulse 1.5s ease-out infinite",
              }}
            />
          )}
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: "50% 50% 50% 0",
              transform: "rotate(-45deg)",
              background: i === 0 ? BRAND_BLUE : "rgba(59,130,246,0.65)",
              boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid #fff",
            }}
          >
            <span style={{ transform: "rotate(45deg)", color: "#fff", fontSize: 14, fontWeight: 800 }}>
              ⌖
            </span>
          </div>
        </div>
      ))}

      {newest && (
        <div
          key={`callout-${newest.key}`}
          style={{
            position: "absolute",
            left: 12,
            bottom: 12,
            right: 12,
            maxWidth: 340,
            background: "rgba(255,255,255,0.97)",
            backdropFilter: "blur(4px)",
            borderRadius: 12,
            border: "1px solid #e2e8f0",
            boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
            padding: "0.65rem 0.8rem",
            display: "flex",
            alignItems: "center",
            gap: "0.65rem",
            animation: "slideInRight 0.5s ease both",
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              background: `${BRAND_BLUE}1a`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              color: BRAND_BLUE,
              fontSize: 18,
            }}
          >
            🔔
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: BRAND_BLUE,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {newest.addr} just scanned
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: "#64748b",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {newest.suburb} · {newest.id}
            </div>
          </div>
        </div>
      )}

      {/* Status badge top-right */}
      <div
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          background: "rgba(15,23,42,0.85)",
          color: "#fff",
          padding: "5px 10px",
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: BRAND_ACCENT_COLOR,
            boxShadow: `0 0 0 4px ${BRAND_ACCENT_COLOR}33`,
          }}
        />
        LIVE SCANS
      </div>
    </div>
  );
}


/* ─────────── Street walking animation ─────────── */
function StreetMap({ isMobile }) {
  const odds = useMemo(() => [11, 13, 15, 17, 19, 21], []);
  const evens = useMemo(() => [12, 14, 16, 18, 20, 22], []);
  // 3 of the 12 are rentals and get auto-skipped from the walking route
  const rentals = useMemo(() => new Set([15, 18, 20]), []);
  const order = useMemo(
    () => [...odds, ...[...evens].reverse()].filter((n) => !rentals.has(n)),
    [odds, evens, rentals]
  );

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % order.length), 1100);
    return () => clearInterval(t);
  }, [order.length]);

  const currentNum = order[idx];
  const visited = new Set(order.slice(0, idx + 1));

  const isOddPos = currentNum % 2 === 1;
  const colIdx = isOddPos ? odds.indexOf(currentNum) : evens.indexOf(currentNum);
  const cols = 6;
  const leftPct = ((colIdx + 0.5) / cols) * 100;
  const topPct = isOddPos ? 30 : 70;

  const houseStyle = (num) => {
    const isCurrent = num === currentNum;
    const wasVisited = visited.has(num);
    const isRental = rentals.has(num);
    if (isRental) {
      return {
        width: "100%",
        aspectRatio: "1 / 1",
        borderRadius: 8,
        border: "2px dashed rgba(248,113,113,0.5)",
        background: "rgba(127,29,29,0.18)",
        color: "rgba(252,165,165,0.7)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 800,
        fontSize: 13,
        transition: "all 0.45s ease",
        position: "relative",
        textDecoration: "line-through",
        textDecorationThickness: 1.5,
      };
    }
    return {
      width: "100%",
      aspectRatio: "1 / 1",
      borderRadius: 8,
      border: `2px solid ${
        isCurrent ? BRAND_ACCENT_COLOR : wasVisited ? "#cbd5e1" : "#1e293b"
      }`,
      background: isCurrent
        ? "rgba(16,185,129,0.18)"
        : wasVisited
        ? "rgba(148,163,184,0.15)"
        : "rgba(15,23,42,0.55)",
      color: isCurrent ? BRAND_ACCENT_COLOR : wasVisited ? "#64748b" : "#cbd5e1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 800,
      fontSize: 13,
      transition: "all 0.45s ease",
      boxShadow: isCurrent ? "0 0 0 4px rgba(16,185,129,0.18)" : "none",
      position: "relative",
    };
  };

  const RentalBadge = () => (
    <span
      style={{
        position: "absolute",
        top: -7,
        right: -4,
        background: "#dc2626",
        color: "#fff",
        fontSize: 7,
        fontWeight: 800,
        letterSpacing: 0.4,
        padding: "1px 5px",
        borderRadius: 4,
        textDecoration: "none",
        textTransform: "uppercase",
      }}
    >
      RENTED
    </span>
  );

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
        borderRadius: 16,
        padding: isMobile ? "1.25rem" : "1.75rem",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.35fr 1fr",
          gap: isMobile ? 16 : 22,
          position: "relative",
          zIndex: 1,
        }}
      >
        <div>
          <div
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1,
              marginBottom: 10,
              textTransform: "uppercase",
            }}
          >
            Sample street · door-knock route
          </div>
          <div
            style={{
              position: "relative",
              padding: "8px 0",
              borderRadius: 12,
              background: "rgba(255,255,255,0.03)",
              border: "1px dashed rgba(255,255,255,0.12)",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: 8,
                padding: "10px 14px",
              }}
            >
              {odds.map((n) => (
                <div key={n} style={houseStyle(n)}>
                  {n}
                  {rentals.has(n) && <RentalBadge />}
                </div>
              ))}
            </div>
            <div
              style={{
                margin: "4px 14px",
                height: 10,
                borderRadius: 4,
                background:
                  "repeating-linear-gradient(90deg, rgba(255,255,255,0.18) 0 12px, transparent 12px 24px)",
              }}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: 8,
                padding: "10px 14px",
              }}
            >
              {evens.map((n) => (
                <div key={n} style={houseStyle(n)}>
                  {n}
                  {rentals.has(n) && <RentalBadge />}
                </div>
              ))}
            </div>

            <div
              style={{
                position: "absolute",
                top: `${topPct}%`,
                left: `calc(14px + (100% - 28px) * ${leftPct / 100})`,
                transform: "translate(-50%, -50%)",
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: BRAND_ACCENT_COLOR,
                border: "3px solid #fff",
                boxShadow: "0 0 0 4px rgba(16,185,129,0.35)",
                transition: "top 0.7s ease, left 0.7s ease",
                animation: "walkerBob 1s ease-in-out infinite",
                zIndex: 2,
              }}
            />
          </div>

          <div
            style={{
              marginTop: 14,
              padding: "10px 12px",
              borderRadius: 10,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#e2e8f0",
              fontSize: 11.5,
              lineHeight: 1.55,
            }}
          >
            <span style={{ color: BRAND_ACCENT_COLOR, fontWeight: 700 }}>
              Walking order:
            </span>{" "}
            up the odd side, then back down the evens. PDF pages come out in
            the same order so agents walk in a straight line and never break
            stride.{" "}
            <span style={{ color: "#fca5a5", fontWeight: 700 }}>
              Three rentals (#15, #18, #20) auto-skipped
            </span>{" "}
            so you don't waste flyers on tenants.
          </div>
        </div>

        <div>
          <div
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1,
              marginBottom: 10,
              textTransform: "uppercase",
            }}
          >
            PDF · page {idx + 1} of {order.length}
          </div>
          <div
            key={currentNum}
            style={{
              background: "#fff",
              borderRadius: 10,
              padding: 14,
              animation: "fadeUp 0.45s ease both",
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                color: BRAND_BLUE,
                letterSpacing: 1,
              }}
            >
              UNIQUE TO #{currentNum}
            </div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "#0f172a",
                marginTop: 2,
                marginBottom: 2,
              }}
            >
              {currentNum} Sample Street
            </div>
            <div style={{ fontSize: 10, color: "#64748b", letterSpacing: 0.5 }}>
              SUBURB · STATE 2000
            </div>

            <div
              style={{
                marginTop: 12,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <MiniQr seed={`sample-${currentNum}`} size={104} fg="#0f172a" />
            </div>
            <div
              style={{
                marginTop: 6,
                textAlign: "center",
                fontSize: 8.5,
                color: BRAND_BLUE,
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              SCAN FOR THIS PROPERTY'S ESTIMATE
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 4,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {order.map((n, i) => (
              <div
                key={n}
                style={{
                  width: 22,
                  height: 28,
                  borderRadius: 3,
                  background:
                    i === idx
                      ? BRAND_ACCENT_COLOR
                      : i < idx
                      ? "rgba(255,255,255,0.45)"
                      : "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  fontSize: 9,
                  color: i === idx ? "#fff" : "#cbd5e1",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.4s ease",
                }}
              >
                {n}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Generate-steps animation ─────────── */
const GENERATE_STEPS = [
  {
    kicker: "Step 1",
    title: "Search a street",
    blurb:
      "Start typing — we autocomplete real Australian streets and pull every property on the one you pick.",
  },
  {
    kicker: "Step 2",
    title: "Filter to your route",
    blurb:
      "Narrow by number range, min beds, property type, odds/evens, and exclude currently-rented homes.",
  },
  {
    kicker: "Step 3",
    title: "Customise the copy",
    blurb:
      "Tweak the headline, trust chips and scan prompt. Defaults are agency-branded and ready to print.",
  },
  {
    kicker: "Step 4",
    title: "Generate the PDF",
    blurb:
      "One A4 page per property, each with its own QR. Download, print, and start the door knock.",
  },
];

function StepBrowserFrame({ children }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
        width: "100%",
        minWidth: 0,
      }}
    >
      <div
        style={{
          background: "#f1f5f9",
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          gap: 6,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fca5a5", flexShrink: 0 }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fcd34d", flexShrink: 0 }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#86efac", flexShrink: 0 }} />
        <div
          style={{
            marginLeft: 10,
            flex: 1,
            minWidth: 0,
            background: "#fff",
            borderRadius: 6,
            padding: "3px 10px",
            fontSize: 10.5,
            color: "#64748b",
            border: "1px solid #e2e8f0",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          www.vendorleads.com.au/campaign-qr-code
        </div>
      </div>
      <div style={{ padding: 16, height: 380, overflow: "hidden" }}>{children}</div>
    </div>
  );
}

function Step1Search() {
  const [typed, setTyped] = useState("");
  const target = "Sevira Avenue, Gledswood Hills";
  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % (target.length + 18);
      setTyped(target.slice(0, Math.min(i, target.length)));
    }, 80);
    return () => clearInterval(t);
  }, []);
  const suggestions = [
    "Sevira Avenue, Gledswood Hills NSW 2557",
    "Sevenoaks Street, Bondi NSW 2026",
    "Seventh Street, Mascot NSW 2020",
  ];
  return (
    <div style={{ animation: "fadeUp 0.4s ease both" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
        1. Find a street
      </div>
      <div
        style={{
          position: "relative",
          border: "1px solid #2563eb",
          borderRadius: 8,
          padding: "10px 12px 10px 32px",
          background: "#fff",
          fontSize: 13,
          color: "#0f172a",
          boxShadow: "0 0 0 3px rgba(37,99,235,0.15)",
        }}
      >
        <span style={{ position: "absolute", left: 11, top: 9, color: "#94a3b8" }}>🔍</span>
        {typed}
        <span
          style={{
            display: "inline-block",
            width: 1,
            height: 14,
            background: "#0f172a",
            verticalAlign: "middle",
            marginLeft: 1,
            animation: "qrPulse 0.8s steps(2) infinite",
          }}
        />
      </div>
      <div
        style={{
          marginTop: 6,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        {suggestions.map((s, i) => (
          <div
            key={s}
            style={{
              padding: "9px 12px",
              fontSize: 12.5,
              borderBottom: i < suggestions.length - 1 ? "1px solid #f3f4f6" : "none",
              color: "#111827",
              background: i === 0 ? "#eff6ff" : "#fff",
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <span style={{ color: BRAND_BLUE }}>📍</span>
            {s}
          </div>
        ))}
      </div>
    </div>
  );
}

function Step2Filter() {
  return (
    <div style={{ animation: "fadeUp 0.4s ease both" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
        2. 24 of 38 properties — refine with filters
      </div>
      <div
        style={{
          background: "#f9fafb",
          borderRadius: 8,
          padding: 10,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 8,
        }}
      >
        {[
          ["From #", "1"],
          ["To #", "60"],
          ["Min beds", "3"],
        ].map(([l, v]) => (
          <div key={l}>
            <div style={{ fontSize: 9.5, color: "#6b7280", fontWeight: 500, marginBottom: 3 }}>
              {l}
            </div>
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                padding: "5px 8px",
                fontSize: 12,
                color: "#0f172a",
                fontWeight: 600,
              }}
            >
              {v}
            </div>
          </div>
        ))}
      </div>
      {[
        {
          label: "Show",
          opts: [["All", false], ["Rented", false], ["Owner occupied", true]],
        },
        {
          label: "Street numbers",
          opts: [["All", false], ["Odd", true], ["Even", false]],
        },
        {
          label: "Property type",
          opts: [["House", true], ["Townhouse", false], ["Unit", false]],
        },
      ].map((group) => (
        <div key={group.label} style={{ marginTop: 10 }}>
          <div style={{ fontSize: 9.5, color: "#6b7280", fontWeight: 500, marginBottom: 4 }}>
            {group.label}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {group.opts.map(([l, active]) => (
              <span
                key={l}
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: `1px solid ${active ? BRAND_BLUE : "#e5e7eb"}`,
                  background: active ? "#eff6ff" : "#fff",
                  color: active ? BRAND_BLUE : "#374151",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {l}
              </span>
            ))}
          </div>
        </div>
      ))}
      <div
        style={{
          marginTop: 10,
          padding: "6px 10px",
          background: "rgba(16,185,129,0.1)",
          borderRadius: 6,
          fontSize: 11,
          color: "#047857",
          fontWeight: 600,
        }}
      >
        ✓ 24 properties match · rentals auto-skipped
      </div>
    </div>
  );
}

function Step3Customise() {
  const fields = [
    ["Headline", "See what this home is really worth, instantly"],
    ["Chip 1", "INSTANT"],
    ["Chip 2", "FREE"],
    ["Scan prompt", "Scan with your phone camera"],
  ];
  return (
    <div style={{ animation: "fadeUp 0.4s ease both" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
        3. Customise PDF copy
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {fields.map(([l, v], i) => (
          <div key={l} style={i === 0 ? { gridColumn: "1 / -1" } : null}>
            <div style={{ fontSize: 9.5, color: "#6b7280", fontWeight: 500, marginBottom: 3 }}>
              {l}
            </div>
            <div
              style={{
                background: "#fff",
                border: `1px solid ${i === 0 ? BRAND_BLUE : "#e5e7eb"}`,
                borderRadius: 6,
                padding: "6px 9px",
                fontSize: 11.5,
                color: "#0f172a",
                boxShadow: i === 0 ? `0 0 0 3px ${BRAND_BLUE}26` : "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {v}
              {i === 0 && (
                <span
                  style={{
                    display: "inline-block",
                    width: 1,
                    height: 12,
                    background: "#0f172a",
                    verticalAlign: "middle",
                    marginLeft: 1,
                    animation: "qrPulse 0.8s steps(2) infinite",
                  }}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CONFETTI_PIECES = Array.from({ length: 26 }).map((_, i) => {
  const palette = ["#10b981", "#3b82f6", "#f59e0b", "#ec4899", "#8b5cf6", "#ef4444"];
  const angle = (i / 26) * Math.PI - Math.PI / 2;
  const dist = 90 + Math.random() * 80;
  return {
    id: i,
    color: palette[i % palette.length],
    cx: `${Math.cos(angle) * dist}px`,
    cy: `${Math.sin(angle) * dist + 40}px`,
    cr: `${(Math.random() * 720 - 360).toFixed(0)}deg`,
    delay: `${(Math.random() * 0.15).toFixed(2)}s`,
    left: `${48 + (Math.random() * 8 - 4)}%`,
    size: 5 + Math.round(Math.random() * 5),
  };
});

function Step4Generate() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () =>
        setProgress((p) => {
          if (p >= 100) {
            clearInterval(t);
            return 100;
          }
          return p + 4;
        }),
      80
    );
    return () => clearInterval(t);
  }, []);
  const done = progress >= 100;
  return (
    <div style={{ animation: "fadeUp 0.4s ease both", position: "relative" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 10 }}>
        4. Generate PDF (24 selected)
      </div>
      <button
        style={{
          background: done ? BRAND_ACCENT_COLOR : "#7c3aed",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          padding: "10px 16px",
          fontSize: 13,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "default",
        }}
      >
        {done ? "✓ PDF ready" : "📄 Generating PDF…"}
      </button>
      <div
        style={{
          marginTop: 12,
          height: 6,
          borderRadius: 999,
          background: "#e5e7eb",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${progress}%`,
            background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_ACCENT_COLOR})`,
            transition: "width 0.08s linear",
          }}
        />
      </div>

      {/* Confetti burst — fires once when done */}
      {done && (
        <div
          style={{
            position: "absolute",
            top: 70,
            left: 0,
            right: 0,
            height: 0,
            pointerEvents: "none",
            zIndex: 5,
          }}
        >
          {CONFETTI_PIECES.map((c) => (
            <span
              key={c.id}
              style={{
                position: "absolute",
                left: c.left,
                top: 0,
                width: c.size,
                height: c.size * 1.6,
                background: c.color,
                borderRadius: 1,
                "--cx": c.cx,
                "--cy": c.cy,
                "--cr": c.cr,
                animation: `confettiFall 1.1s ease-out ${c.delay} forwards`,
              }}
            />
          ))}
        </div>
      )}

      {/* Chrome-style download notification (slides up from bottom) */}
      {done && (
        <div
          style={{
            marginTop: 60,
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              boxShadow: "0 10px 28px rgba(15,23,42,0.18)",
              padding: "8px 12px 8px 10px",
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              maxWidth: "100%",
              animation: "toastSlideUp 0.5s cubic-bezier(0.2, 0.9, 0.3, 1.2) both",
            }}
          >
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div
                style={{
                  width: 34,
                  height: 42,
                  borderRadius: 4,
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#dc2626",
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: 0.3,
                }}
              >
                PDF
              </div>
              <div
                style={{
                  position: "absolute",
                  right: -6,
                  bottom: -4,
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background: BRAND_ACCENT_COLOR,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 800,
                  border: "2px solid #fff",
                  boxShadow: "0 2px 6px rgba(16,185,129,0.4)",
                  animation: "tickPop 0.45s cubic-bezier(0.2, 0.9, 0.3, 1.4) 0.15s both",
                }}
              >
                ✓
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: "#0f172a",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                property_qr_cards.pdf
              </div>
              <div style={{ fontSize: 10.5, color: "#059669", fontWeight: 600 }}>
                Downloaded · 24 pages
              </div>
            </div>
            <span
              style={{
                color: "#94a3b8",
                fontSize: 14,
                padding: "0 4px",
                cursor: "default",
              }}
            >
              ⌄
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function GenerateStepsAnimation({ isMobile }) {
  const [step, setStep] = useState(0);
  const [cycleToken, setCycleToken] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % GENERATE_STEPS.length), 3600);
    return () => clearInterval(t);
  }, [cycleToken]);
  const handleSelectStep = (i) => {
    setStep(i);
    setCycleToken((c) => c + 1);
  };

  const StepBody = [Step1Search, Step2Filter, Step3Customise, Step4Generate][step];

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
        borderRadius: 16,
        padding: isMobile ? "1.5rem 1.25rem" : "2rem",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1.1fr",
          gap: isMobile ? 20 : 32,
          position: "relative",
          zIndex: 1,
          alignItems: "center",
        }}
      >
        {/* Step list */}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1.5,
              marginBottom: 14,
              textTransform: "uppercase",
            }}
          >
            From street name to printed PDF · about a minute
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {GENERATE_STEPS.map((s, i) => {
              const active = i === step;
              const done = i < step;
              return (
                <button
                  key={s.title}
                  type="button"
                  onClick={() => handleSelectStep(i)}
                  style={{
                    display: "flex",
                    gap: 12,
                    borderRadius: 12,
                    padding: isMobile ? "10px 12px" : "12px 14px",
                    background: active
                      ? "rgba(16,185,129,0.12)"
                      : "rgba(255,255,255,0.04)",
                    border: `1px solid ${
                      active
                        ? "rgba(16,185,129,0.45)"
                        : "rgba(255,255,255,0.08)"
                    }`,
                    transition: "all 0.4s ease",
                    opacity: done ? 0.7 : 1,
                    cursor: "pointer",
                    textAlign: "left",
                    font: "inherit",
                    color: "inherit",
                    width: "100%",
                  }}
                  onMouseOver={(e) => {
                    if (!active) {
                      e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!active) {
                      e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    }
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: active
                        ? BRAND_ACCENT_COLOR
                        : done
                        ? "rgba(16,185,129,0.55)"
                        : "rgba(255,255,255,0.1)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 800,
                      flexShrink: 0,
                      boxShadow: active
                        ? "0 0 0 4px rgba(16,185,129,0.25)"
                        : "none",
                    }}
                  >
                    {done ? "✓" : i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}>
                    {isMobile ? (
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#fff",
                        }}
                      >
                        {s.title}
                      </div>
                    ) : (
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: 1.2,
                            color: active ? BRAND_ACCENT_COLOR : "rgba(255,255,255,0.55)",
                            textTransform: "uppercase",
                          }}
                        >
                          {s.kicker}
                        </div>
                        <div
                          style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: "#fff",
                            margin: "2px 0 3px 0",
                          }}
                        >
                          {s.title}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "rgba(255,255,255,0.65)",
                            lineHeight: 1.5,
                          }}
                        >
                          {s.blurb}
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Animated browser */}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1.5,
              marginBottom: 10,
              textTransform: "uppercase",
            }}
          >
            Live preview · step {step + 1} of {GENERATE_STEPS.length}
          </div>
          <StepBrowserFrame>
            <div key={step}>
              <StepBody />
            </div>
          </StepBrowserFrame>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Scans chart + live feed (bottom) ─────────── */
function ScanChartAndFeed({ isMobile }) {
  const data = [
    { day: "Mon", p: 4 },
    { day: "Tue", p: 7 },
    { day: "Wed", p: 9 },
    { day: "Thu", p: 6 },
    { day: "Fri", p: 14 },
    { day: "Sat", p: 21 },
    { day: "Sun", p: 17 },
  ];
  const maxV = Math.max(...data.map((d) => d.p));
  const chartHeight = isMobile ? 180 : 240;

  const [feed, setFeed] = useState(() =>
    SCAN_FEED_TEMPLATES.slice(0, 5).map((t, i) => ({
      ...t,
      key: `seed-${i}`,
      secondsAgo: (i + 1) * 47,
    }))
  );
  const counter = useRef(0);

  useEffect(() => {
    const t = setInterval(() => {
      counter.current += 1;
      const idx = counter.current % SCAN_FEED_TEMPLATES.length;
      const next = {
        ...SCAN_FEED_TEMPLATES[idx],
        key: `scan-${counter.current}`,
        secondsAgo: 0,
      };
      setFeed((prev) =>
        [next, ...prev.map((p) => ({ ...p, secondsAgo: p.secondsAgo + 2 }))].slice(0, 7)
      );
    }, 2000);
    return () => clearInterval(t);
  }, []);

  const formatAgo = (s) => {
    if (s < 5) return "just now";
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    return `${m} min ago`;
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        gap: 20,
        alignItems: "stretch",
      }}
    >
      {/* Chart */}
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #e5e7eb",
          padding: isMobile ? "16px 14px" : "22px 26px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937" }}>
            Per-property scans this week
          </div>
          <div style={{ fontSize: 11, color: "#6b7280" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: BRAND_BLUE }} />
              Unique scans
            </span>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${data.length}, 1fr)`,
            gap: 10,
            alignItems: "end",
            height: chartHeight,
            borderBottom: "1px solid #f1f5f9",
            padding: "0 4px 6px 4px",
          }}
        >
          {data.map((d, i) => (
            <div
              key={d.day}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                height: "100%",
                justifyContent: "flex-end",
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 700, color: "#475569" }}>{d.p}</div>
              <div
                style={{
                  width: "55%",
                  height: `${(d.p / maxV) * 100}%`,
                  background: BRAND_BLUE,
                  borderRadius: "4px 4px 0 0",
                  transformOrigin: "bottom",
                  animation: `barGrow 0.7s ${i * 60}ms ease both`,
                }}
              />
            </div>
          ))}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${data.length}, 1fr)`,
            gap: 10,
            marginTop: 6,
          }}
        >
          {data.map((d) => (
            <div
              key={d.day}
              style={{ fontSize: 10, color: "#94a3b8", textAlign: "center" }}
            >
              {d.day}
            </div>
          ))}
        </div>
      </div>

      {/* Live feed */}
      <div
        style={{
          background: "#0f172a",
          borderRadius: 16,
          padding: isMobile ? "16px 14px" : "20px 22px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700 }}>Live scan feed</div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
              color: BRAND_ACCENT_COLOR,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: BRAND_ACCENT_COLOR,
                boxShadow: `0 0 0 4px ${BRAND_ACCENT_COLOR}33`,
              }}
            />
            LIVE
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            overflow: "hidden",
            minHeight: 7 * 48 + 6 * 8,
          }}
        >
          {feed.map((item, i) => (
            <div
              key={item.key}
              style={{
                background:
                  i === 0
                    ? "rgba(59,130,246,0.18)"
                    : "rgba(255,255,255,0.05)",
                border: `1px solid ${
                  i === 0 ? "rgba(59,130,246,0.4)" : "rgba(255,255,255,0.08)"
                }`,
                borderRadius: 10,
                padding: "9px 11px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                animation: i === 0 ? "slideInDown 0.45s ease both" : "none",
                opacity: i === 0 ? 1 : Math.max(0.55, 1 - i * 0.08),
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: BRAND_BLUE,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 15,
                  flexShrink: 0,
                }}
              >
                🔔
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.addr}
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    color: "rgba(255,255,255,0.55)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.suburb}
                </div>
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: i === 0 ? BRAND_ACCENT_COLOR : "rgba(255,255,255,0.5)",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {formatAgo(item.secondsAgo)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────── Section heading ─────────── */
function SectionHeading({ kicker, title, blurb, accent }) {
  return (
    <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 2.5rem auto" }}>
      <div
        style={{
          display: "inline-block",
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: 2,
          color: accent,
          background: `${accent}1a`,
          padding: "4px 12px",
          borderRadius: 999,
          marginBottom: 12,
          textTransform: "uppercase",
        }}
      >
        {kicker}
      </div>
      <h2
        style={{
          fontSize: "1.9rem",
          fontWeight: 800,
          color: BRAND_COLOR,
          margin: "0 0 0.75rem 0",
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
        }}
      >
        {title}
      </h2>
      <p style={{ color: GRAY_DARK_COLOR, fontSize: "1rem", lineHeight: 1.65, margin: 0 }}>
        {blurb}
      </p>
    </div>
  );
}

/* ─────────── Main ─────────── */
export default function QRCodeFlyer() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showContact, setShowContact] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div style={{ fontFamily: "Inter, Arial, sans-serif", background: "#fff" }}>
      <GlobalAnimations />
      <Navbar onSignIn={() => setShowSignIn(true)} />

      {/* ─────────── Live map at top ─────────── */}
      <section
        style={{
          background:
            "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #134e4a 100%)",
          padding: isMobile ? "2.5rem 1.25rem 3rem" : "4rem 2rem 4.5rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            pointerEvents: "none",
          }}
        />
        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(59,130,246,0.18)",
                border: "1px solid rgba(59,130,246,0.4)",
                color: "#93c5fd",
                fontSize: "0.85rem",
                fontWeight: 600,
                borderRadius: 999,
                padding: "0.4rem 1rem",
                marginBottom: "1.1rem",
                letterSpacing: "0.03em",
              }}
            >
              Per-Property QR · Address-level tracking
            </div>
            <h1
              style={{
                fontSize: isMobile ? "1.9rem" : "2.6rem",
                fontWeight: 800,
                marginBottom: "0.85rem",
                lineHeight: 1.15,
                color: "#fff",
                letterSpacing: "-0.02em",
              }}
            >
              Know exactly who scanned your flyer
            </h1>
            <p
              style={{
                fontSize: isMobile ? "1rem" : "1.1rem",
                color: "rgba(255,255,255,0.72)",
                lineHeight: 1.6,
                maxWidth: 620,
                margin: "0 auto",
              }}
            >
              Every PDF page is unique to one property and carries its own QR.
              When that home scans, you get pinged with the address.
            </p>
          </div>

          <LiveScanMap isMobile={isMobile} />

          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button
              style={{
                border: "none",
                borderRadius: 8,
                padding: "0.85rem 2rem",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: "pointer",
                background: BRAND_ACCENT_COLOR,
                color: "#fff",
                boxShadow: "0 4px 14px rgba(16,185,129,0.4)",
                transition: "transform 0.15s, box-shadow 0.15s",
              }}
              onClick={() => setShowSignIn(true)}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(16,185,129,0.5)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,185,129,0.4)";
              }}
            >
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* ─────────── Live demo flyer (Demo Agency / 22 Sevira) ─────────── */}
      <section
        style={{
          padding: isMobile ? "3rem 1.5rem" : "5rem 2rem",
          background: "#fff",
        }}
      >
        <SectionHeading
          kicker="One page, one property"
          accent={BRAND_ACCENT_COLOR}
          title="Print-ready, fully agency-branded"
          blurb="Every page renders in your agency's colours with your logo, agent photo and contact details baked in. Listing photo, beds, baths, cars and the property's own unique QR code, all generated automatically."
        />
        <div
          style={{
            maxWidth: 1000,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "auto 1fr",
            gap: isMobile ? 24 : 56,
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", justifyContent: "center" }}>
            <DemoPropertyFlyer width={isMobile ? 240 : 300} />
          </div>
          <div>
            <h3
              style={{
                fontSize: "1.3rem",
                fontWeight: 700,
                color: BRAND_COLOR,
                margin: "0 0 14px 0",
              }}
            >
              Anatomy of the page
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 10,
              }}
            >
              {[
                ["Agency bar", "Your logo and agency name in your brand colour"],
                ["Hero photo", "The property's own listing photo"],
                ["Address strip", "Full address, suburb, state and postcode"],
                ["Trust chips", "Instant, free, no sign-up"],
                ["Unique QR", "Encodes this property's id, not the suburb"],
                ["Agent footer", "Photo, name, title, phone and email"],
              ].map(([t, d]) => (
                <div
                  key={t}
                  style={{
                    background: "#eff6ff",
                    borderRadius: 10,
                    padding: "10px 12px",
                    border: "1px solid #dbeafe",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: BRAND_BLUE,
                    }}
                  >
                    {t}
                  </div>
                  <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>
                    {d}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── Generate steps (animated walkthrough) ─────────── */}
      <section
        style={{
          padding: isMobile ? "3rem 1.5rem" : "5rem 2rem",
          background: "#fff",
          borderTop: "1px solid #eef2f7",
        }}
      >
        <SectionHeading
          kicker="How to generate"
          accent={BRAND_ACCENT_COLOR}
          title="Four steps from street name to printed PDF"
          blurb="Inside the dashboard, generating a per-property QR PDF takes about a minute. Here's the exact flow."
        />
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <GenerateStepsAnimation isMobile={isMobile} />
        </div>
      </section>

      {/* ─────────── How it works (walking animation) ─────────── */}
      <section
        style={{
          padding: isMobile ? "3rem 1.5rem" : "5rem 2rem",
          background: "#f8fafc",
          borderTop: "1px solid #eef2f7",
          borderBottom: "1px solid #eef2f7",
        }}
      >
        <SectionHeading
          kicker="How it works"
          accent={BRAND_ACCENT_COLOR}
          title="A PDF that walks the street with you"
          blurb="Pick a street, we pull every property on it. Each page is one home with its own unique QR, and the pages come out in walking order."
        />
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <StreetMap isMobile={isMobile} />

          <div
            style={{
              marginTop: 28,
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
              gap: 16,
            }}
          >
            {[
              { kicker: "Unique per home", title: "Every QR is one-of-one" },
              { kicker: "Instant notification", title: "Know the moment they scan" },
              { kicker: "Walking order", title: "Odds up, evens back" },
              { kicker: "Street search", title: "Pick any street" },
              { kicker: "Smart filters", title: "Narrow to your route" },
              { kicker: "Rentals auto-skipped", title: "Owners only, by default" },
            ].map((f) => (
              <div
                key={f.title}
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                  padding: "16px 18px",
                  boxShadow: "0 2px 10px rgba(15,23,42,0.04)",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: 1.5,
                    color: BRAND_BLUE,
                    textTransform: "uppercase",
                    marginBottom: 6,
                  }}
                >
                  {f.kicker}
                </div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: BRAND_COLOR,
                  }}
                >
                  {f.title}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── Tracking (chart + live feed) ─────────── */}
      <section
        style={{
          padding: isMobile ? "3rem 1.5rem" : "5rem 2rem",
          background: "#f8fafc",
          borderTop: "1px solid #eef2f7",
        }}
      >
        <SectionHeading
          kicker="Every scan is tracked"
          accent={BRAND_ACCENT_COLOR}
          title="Watch your campaign light up in real time"
          blurb="Scans roll into the dashboard the second they happen, attributed to the exact property. Volume on the left, live feed on the right."
        />
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <ScanChartAndFeed isMobile={isMobile} />
        </div>
      </section>

      {/* ─────────── CTA ─────────── */}
      <section
        style={{
          background:
            "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #134e4a 100%)",
          padding: isMobile ? "3.5rem 1.5rem" : "5rem 2rem",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            pointerEvents: "none",
          }}
        />
        <div style={{ maxWidth: 600, margin: "0 auto", position: "relative", zIndex: 1 }}>
          <h2
            style={{
              fontSize: isMobile ? "1.8rem" : "2.4rem",
              fontWeight: 800,
              color: "#fff",
              marginBottom: "1rem",
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
            }}
          >
            Walk the street. Drop the flyer. Wait for the ping.
          </h2>
          <p
            style={{
              color: "rgba(255,255,255,0.65)",
              marginBottom: "2rem",
              fontSize: "1.1rem",
              lineHeight: 1.6,
            }}
          >
            Generate a street's worth of per-property pages in under a minute.
            Every page is its own QR. Every scan tells you the address.
          </p>
          <button
            style={{
              border: "none",
              borderRadius: 8,
              padding: "0.9rem 2.5rem",
              fontSize: "1.05rem",
              fontWeight: 600,
              cursor: "pointer",
              background: BRAND_ACCENT_COLOR,
              color: "#fff",
              boxShadow: "0 4px 14px rgba(16,185,129,0.4)",
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            onClick={() => setShowSignIn(true)}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(16,185,129,0.5)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,185,129,0.4)";
            }}
          >
            Get Started
          </button>
        </div>
      </section>

      {showSignIn && <SignInModal show={showSignIn} onHide={() => setShowSignIn(false)} />}
      {showContact && <ContactVendorleadsModal show={showContact} onHide={() => setShowContact(false)} />}
    </div>
  );
}
